import { TableColumn } from "react-data-table-component";
import { Fragment, useState } from "react";
import { Badges, Btn } from "../../AbstractElements";
import { RefreshCcw } from "react-feather";
import { CustomCellInterFace } from "../../Types/TablesType";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

//Modals
import UpdateDepartmentModal from "../../Components/UiKits/Modal/Department/Update";

//Services
import {
  VIEW_DEPARTMENT_QUERY_KEY,
  DEPARTMENT_QUERY_KEY,
  departmentService,
} from "../../Services/departmentService";

//Types
import { ViewDepartments } from "../../Types/CompanyProfile/Department";

const DepartmentActionCell = ({ row }: { row: ViewDepartments }) => {
  const queryClient = useQueryClient();
  const [isRowLoading, setIsRowLoading] = useState<boolean>(false);
  const departmentQueryKey = [...VIEW_DEPARTMENT_QUERY_KEY, row.company_code];
  const handleStatusToggle = () => {
    Swal.fire({
      icon: "warning",
      html: `Are you sure you want to <b>"${
        row.status_label === "ACTIVE" ? "Deactivate " : "Activate"
      }"</b> This Department: <b>"${row.department_name}"</b> ?`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsRowLoading(true);
        toggleMutation.mutate(row.department_id);
      }
    });
  };
  const toggleMutation = useMutation({
    mutationFn: departmentService.toggleStatus,
    onSuccess: async (variables: {
      department_id: number;
      status_label: string;
      department_name: string;
    }) => {
      queryClient.setQueryData<ViewDepartments[]>(
        departmentQueryKey,
        (oldData) =>
          oldData?.map((dept) =>
            dept.department_id === variables.department_id
              ? {
                  ...dept,
                  status_label:
                    dept.status_label === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                }
              : dept,
          ),
      );

      await queryClient.refetchQueries({
        queryKey: departmentQueryKey,
        exact: true,
      });

      toast.success(
        `"${row.department_name}" status has been set to "${
          variables.status_label === "ACTIVE" ? "INACTIVE" : "ACTIVE"
        }".`,
      );
      setIsRowLoading(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to toggle status");
      setIsRowLoading(false);
    },
  });

  return (
    <div className="d-flex align-items-center gap-2">
      {/* Update Modal */}
      <UpdateDepartmentModal id={row.department_id} />
      {/* StatusToggle Button */}
      <Btn
        size="sm"
        color={row.status_label === "ACTIVE" ? "danger" : "success"}
        className="px-2"
        onClick={handleStatusToggle}
        disabled={isRowLoading}
      >
        {isRowLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Loading...
          </>
        ) : (
          <>
            <RefreshCcw size={11} className="me-1" />
            {row.status_label === "ACTIVE" ? "Disable" : <>Enable&nbsp;</>}
          </>
        )}
      </Btn>
    </div>
  );
};

const CustomBadge: React.FC<CustomCellInterFace> = ({ label, color }) => (
  <Badges pill color={color}>
    {label}
  </Badges>
);

export const DepartmentTableColumn: TableColumn<ViewDepartments>[] = [
  {
    name: "DIVISION",
    selector: (row) => row.division_name,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>{`${row.division_name}`}</div>
    ),
    sortable: true,
    center: false,
  },
  {
    name: "DEPARTMENTS",
    selector: (row) => row.department_name,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>{row.department_name}</div>
    ),
    sortable: true,
    center: false,
  },
  {
    name: "STATUS",
    selector: (row) => row.status_label,
    cell: (row) => (
      <>
        <CustomBadge
          color={row.status_label === "ACTIVE" ? "success" : "danger"}
          label={row.status_label}
        />
      </>
    ),
    width: "100px",
    sortable: true,
    center: false,
  },
  {
    name: "DATE CREATED",
    selector: (row) => row.created_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.created_by_user}
        {"\n"}
        {row.created_date}
      </div>
    ),
    width: "180px",
    sortable: true,
    center: false,
  },
  {
    name: "DATE MODIFIED",
    selector: (row) => row.updated_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.updated_by_user}
        {"\n"}
        {row.updated_date}
      </div>
    ),
    width: "180px",
    sortable: true,
    center: false,
  },
  {
    name: "ACTION",
    sortable: false,
    center: false,
    cell: (row) => <DepartmentActionCell row={row} />,
    width: "200px",
  },
];
